/**
 * main.js — 時光星球遊戲入口（展示模式）
 *
 * Apple Guideline 3.1.1 合規：外部遊戲只負責 3D 展示 + 分享截圖。
 * P 點升級在 App 內完成，遊戲開啟時偵測等級變化並自動播放升級動畫。
 *
 * Boot sequence:
 *   1. Init Supabase session (JWT from URL)
 *   2. Load island state from DB
 *   3. Detect level changes → auto-play upgrade animation
 *   4. Build Three.js scene + crystal mesh
 *   5. Wire up HUD (progress bar, share button)
 *   6. Start animation loop
 */

import { initSession } from './supabase.js'
import { getOrCreateIsland, getBalance } from './gameService.js'
import { startLoop, addFrameCallback } from './scene.js'
import { createCrystal, update as updateCrystal, pulse as pulseCrystal } from './crystal.js'
import { hideLoading, updateHUD, showToast, celebrateLevelUp, bindShareButton } from './hud.js'
import { shareScreenshot } from './share.js'
import { LEVEL_META } from './levelMeta.js'
import { t } from './i18n/index.js'

// Level names come from levelMeta (the one table) resolved through the
// catalogue — this file used to keep its own copy of all four, so a rename in
// one place silently left the share sheet on the old wording.

// ── App state ─────────────────────────────────────────────────────────────────
let userHash = null
let island = null
let balance = 0

// ── Boot ──────────────────────────────────────────────────────────────────────
async function boot() {
  // 1. Supabase auth
  userHash = await initSession()

  if (!userHash) {
    // Dev / preview mode — use a mock island
    island = { level: 2, exp_points: 0, total_p_spent: 250, visual_state: {} }
    balance = 999
    _buildScene(false)
    hideLoading()
    showToast(t('game.preview_mode', '預覽模式：未連線至帳號'))
    return
  }

  // 2. Load island + balance in parallel
  ;[island, balance] = await Promise.all([getOrCreateIsland(userHash), getBalance(userHash)])

  if (!island) {
    showToast(t('game.load_failed', '無法載入星球資料，請重試'))
    hideLoading()
    return
  }

  // 3. Detect level change → auto-play upgrade animation
  const previousLevel = parseInt(localStorage.getItem('sivecta_last_level') || '0', 10)
  const currentLevel = island.level ?? 1
  const leveledUp = previousLevel > 0 && currentLevel > previousLevel

  // 4. Build scene (with upgrade animation if level changed)
  _buildScene(leveledUp, previousLevel)

  // 5. Save current level for next visit
  localStorage.setItem('sivecta_last_level', String(currentLevel))

  // 6. Hide loading
  hideLoading()
}

// ── Build Three.js scene + HUD ────────────────────────────────────────────────
function _buildScene(leveledUp = false, previousLevel = 0) {
  const level = island?.level ?? 1

  if (leveledUp && previousLevel > 0) {
    // 🎉 Level changed since last visit → play reveal animation
    createCrystal(previousLevel) // Show old level first
    addFrameCallback(updateCrystal)
    startLoop()
    updateHUD(island, balance)
    bindShareButton(_handleShare)

    // After 800ms, transition to new level with celebration
    setTimeout(() => {
      createCrystal(level) // Switch to new level geometry
      addFrameCallback(updateCrystal)
      pulseCrystal() // Pulse animation
      celebrateLevelUp(level) // HUD celebration
      updateHUD(island, balance)
    }, 800)
  } else {
    // Normal load — show current level
    createCrystal(level)
    addFrameCallback(updateCrystal)
    updateHUD(island, balance)
    bindShareButton(_handleShare)
    startLoop()
  }
}

// ── Share handler ─────────────────────────────────────────────────────────────
function _handleShare() {
  const level = island?.level ?? 1
  shareScreenshot({ level, label: t(LEVEL_META[level]?.labelKey ?? 'game.level_1') })
}

// ── Start ─────────────────────────────────────────────────────────────────────
boot().catch((e) => {
  console.error('[main] Boot failed:', e)
  hideLoading()
  document.getElementById('loading').innerHTML =
    `<p style="color:#FF6B6B">${t('game.boot_failed', '載入失敗，請關閉後重試')}</p>`
})
